import type {ReactNode} from 'react'
import type {EventToolCallEntry, ToolGroup} from './types'
import type {ToolCallMetadataMap} from '../../../types/session'
import styles from '../tool/Tool.module.css'
import {formatDuration} from '../../../utils/format-duration'
import {RelativeTime} from '@primer/react'

/**
 * Computes the total duration for a tool group by summing the durations
 * from each entry's tool call metadata. Deduplicates by toolCallId to avoid
 * double-counting when multiple chunks reference the same tool call.
 *
 * If any setup tool in the group is still in-progress (has startedAt but no duration),
 * shows a live elapsed timer from the earliest start time instead of a partial total.
 */
function getToolCallMetadata(entry: unknown): ToolCallMetadataMap | undefined {
  if (typeof entry !== 'object' || entry === null || !('toolCallMetadata' in entry)) return undefined
  return (entry as EventToolCallEntry).toolCallMetadata
}

export function computeGroupDuration(group: ToolGroup<unknown>): ReactNode {
  // Collect per-toolCallId data, preferring completed durations over in-progress
  const toolCallData = new Map<string, {duration?: number; startedAt?: string}>()

  for (const entry of group.entries) {
    const metadata = getToolCallMetadata(entry)
    if (!metadata) continue

    for (const toolCallId of Object.keys(metadata)) {
      const existing = toolCallData.get(toolCallId)
      const duration = metadata[toolCallId]?.duration
      const startedAt = metadata[toolCallId]?.startedAt

      if (duration !== undefined) {
        // Completed duration always wins — only count once per toolCallId
        if (!existing || existing.duration === undefined) {
          toolCallData.set(toolCallId, {duration})
        }
      } else if (startedAt) {
        // Only track as in-progress if no completed duration seen yet
        if (!existing) {
          toolCallData.set(toolCallId, {startedAt})
        } else if (existing.duration === undefined && (!existing.startedAt || startedAt < existing.startedAt)) {
          toolCallData.set(toolCallId, {startedAt})
        }
      }
    }
  }

  let totalDurationMs = 0
  let earliestStartedAt: string | undefined
  let hasInProgress = false
  let hasDurations = false

  for (const data of toolCallData.values()) {
    if (data.duration !== undefined) {
      totalDurationMs += data.duration
      hasDurations = true
    } else if (data.startedAt) {
      hasInProgress = true
      if (!earliestStartedAt || data.startedAt < earliestStartedAt) {
        earliestStartedAt = data.startedAt
      }
    }
  }

  // Show live elapsed timer if any tool is still in-progress
  if (hasInProgress && earliestStartedAt) {
    const adjustedStart =
      totalDurationMs > 0
        ? new Date(new Date(earliestStartedAt).getTime() - totalDurationMs).toISOString()
        : earliestStartedAt

    return (
      <span className={styles.toolDuration}>
        <span className={styles.toolDurationDot} aria-hidden="true">
          ·
        </span>
        <RelativeTime noTitle format="elapsed" datetime={adjustedStart} />
      </span>
    )
  }

  if (!hasDurations) return null

  return (
    <span className={styles.toolDuration}>
      <span className={styles.toolDurationDot} aria-hidden="true">
        ·
      </span>
      {formatDuration(totalDurationMs)}
    </span>
  )
}
